import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Flex,
  Box,
  Image,
  Text,
  IconButton,
  HStack,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@chakra-ui/icons';

const ProductDetailModal = ({ 
  isOpen, 
  onClose, 
  product, 
  onOpenFullImage = () => {} 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');

  const placeholderImage = '/placeholder-product.jpeg';

  // Reset image index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product]);

  // Keyboard navigation for images
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!isOpen || !product || !product.images || product.images.length <= 1) {
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setCurrentImageIndex(currentImageIndex < product.images.length - 1 ? currentImageIndex + 1 : 0);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : product.images.length - 1);
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, currentImageIndex, product]);

  if (!product) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '2xl' }} isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent 
        width={{ base: '100vw', md: '900px' }} 
        height={{ base: '92vh', md: '580px' }} 
        maxW="98vw" 
        maxH="98vh" 
        p={{ base: 2, md: 6 }}
        bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        borderRadius={{ base: 0, md: 'xl' }}
        boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)",
          backgroundSize: "400% 400%",
          animation: "gradientShift 15s ease infinite",
          zIndex: -1,
        }}
        sx={{
          "@keyframes gradientShift": {
            "0%": {
              backgroundPosition: "0% 50%"
            },
            "50%": {
              backgroundPosition: "100% 50%"
            },
            "100%": {
              backgroundPosition: "0% 50%"
            }
          }
        }}
      >
        {/* Close Button */}
        <IconButton
          icon={<CloseIcon />}
          aria-label="Close modal"
          onClick={onClose}
          position="absolute"
          top={{ base: 2, md: 3 }}
          right={{ base: 2, md: 3 }}
          zIndex="2"
          size={{ base: 'sm', md: 'sm' }}
          variant="ghost"
          bg="rgba(255, 255, 255, 0.9)"
          color="gray.700"
          _hover={{ bg: 'rgba(255, 255, 255, 1)', transform: 'scale(1.08)' }}
          _active={{ transform: 'scale(0.95)' }}
          borderRadius="full"
          boxShadow="md"
        />
        <ModalBody 
          p={{ base: 2, md: 4 }} 
          width="100%" 
          height="100%" 
          overflowY="auto"
          bg="rgba(255, 255, 255, 0.95)"
          borderRadius={{ base: 0, md: 'lg' }}
          backdropFilter="blur(10px)"
        >
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 3, md: 6 }}
            justify="center"
            align="flex-start"
            width="100%"
            height="100%"
            pt={{ base: 1, md: 2 }}
          >
            {/* Images Section - Left */}
            <Box
              minWidth={{ base: 'auto', md: '380px' }}
              maxWidth={{ base: '100%', md: '380px' }}
              textAlign="center"
              mb={{ base: 3, md: 0 }}
            >
              <Flex align="center" justify="center" mb={{ base: 2, md: 3 }} gap={2}>
                {product.images && product.images.length > 1 && (
                  <IconButton
                    icon={<ChevronLeftIcon />}
                    aria-label="Previous image"
                    onClick={() => setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : product.images.length - 1)}
                    size={{ base: 'md', md: 'md' }}
                    variant="ghost"
                    color={textColor}
                  />
                )}
                <Image
                  src={product.images && product.images.length > 0 ? product.images[currentImageIndex]?.image : placeholderImage}
                  alt={product.name}
                  boxSize={{ base: '68vw', md: '360px' }}
                  maxH={{ base: '48vh', md: '360px' }}
                  objectFit="cover"
                  borderRadius="md"
                  mx="auto"
                  cursor="pointer"
                  onClick={() => onOpenFullImage(product.images && product.images.length > 0 ? product.images[currentImageIndex]?.image : placeholderImage)}
                  _hover={{ transform: { base: 'none', md: 'scale(1.02)' }, transition: 'transform 0.2s' }}
                />
                {product.images && product.images.length > 1 && (
                  <IconButton
                    icon={<ChevronRightIcon />}
                    aria-label="Next image"
                    onClick={() => setCurrentImageIndex(currentImageIndex < product.images.length - 1 ? currentImageIndex + 1 : 0)}
                    size={{ base: 'md', md: 'md' }}
                    variant="ghost"
                    color={textColor}
                  />
                )}
              </Flex>
              {product.images && product.images.length > 1 && (
                <HStack spacing="2" justify="center" mt="2" overflowX="auto">
                  {product.images.map((img, index) => (
                    <Image
                      key={index}
                      src={img.image}
                      alt={`Thumbnail ${index}`}
                      boxSize={{ base: '48px', md: '56px' }}
                      objectFit="cover"
                      borderRadius="md"
                      cursor="pointer"
                      border={currentImageIndex === index ? '3px solid #4facfe' : '2px solid transparent'}
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                      onDoubleClick={() => onOpenFullImage(img.image)}
                      _hover={{ transform: { base: 'none', md: 'scale(1.05)' }, transition: 'transform 0.2s' }}
                    />
                  ))}
                </HStack>
              )}
            </Box>
            {/* Details Section - Right */}
            <Box flex="1" maxWidth={{ base: '100%', md: '420px' }} height="100%" overflowY="auto">
              <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" mb="2" color={textColor}>
                {product.name}
              </Text>
              <Text fontSize={{ base: 'sm', md: 'md' }} color={subtleTextColor} mb="3">
                {product.description}
              </Text>
              <Divider my="3" />
              <Box>
                <Text fontSize="sm" color={subtleTextColor} mb="2">
                  <Text as="span" fontWeight="600" color={textColor}>Каталожний номер:</Text> {product.catalog_number}
                </Text>
                <Text fontSize="sm" color={subtleTextColor} mb="2">
                  <Text as="span" fontWeight="600" color={textColor}>Одиниця вимірювання:</Text> {product.unit_measurement}
                </Text>
                <Text fontSize="sm" color={subtleTextColor} mb="2">
                  <Text as="span" fontWeight="600" color={textColor}>ПДВ:</Text> {product.is_vat ? 'Так' : 'Ні'}
                </Text>
                <Text fontSize="sm" color={subtleTextColor} mb="2">
                  <Text as="span" fontWeight="600" color={textColor}>Кількість:</Text> {product.stock_quantity}
                </Text>
                <Text fontSize="sm" color={subtleTextColor} mb="2">
                  <Text as="span" fontWeight="600" color={textColor}>Країна виробник:</Text> {product.country_name}
                </Text>
                <Text fontSize="sm" color={subtleTextColor} mb="2">
                  <Text as="span" fontWeight="600" color={textColor}>Закупочна ціна:</Text>{' '}
                  {product.purchase_price
                    ? Number(product.purchase_price).toFixed(2) + ' грн'
                    : '-'}
                </Text>
                <Text fontSize="sm" color={subtleTextColor} mb="2">
                  <Text as="span" fontWeight="600" color={textColor}>Відсоток націнки:</Text>{' '}
                  {product.markup_percentage ? product.markup_percentage + '%' : '-'}
                </Text>
              </Box>
              <Divider my="4" />
              <Box>
                <Text fontSize="xl" fontWeight="bold" color={textColor} mb="2">
                  💰 Ціна: {Number(product.price).toFixed(2)} грн
                </Text>
                {product.is_vat && (
                  <Text fontSize="xl" fontWeight="bold" color="blue.600">
                    💰 Ціна з ПДВ: {Number(product.price_with_vat).toFixed(2)} грн
                  </Text>
                )}
              </Box>
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ProductDetailModal;
